import React, { useCallback } from 'react';
import {
  bool, func, string, arrayOf, exact, number,
} from 'prop-types';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import {
  Flex, Typography, Input, Checkbox,
} from '@atoms';
import { EditWarning } from '@molecules';
import { DraggableItems } from '@organisms';
import { Edit } from '@templates';
import { handleEnterKeyPress } from '@helpers/common';

const SizeProcess = ({
  isDataTheSame,
  onWarningChangeState,
  isWarningOpen,
  onOptionEdit,
  onOptionAdd,
  error,
  sizePrice,
  onSizePreselectedStateChange,
  isSizePreselected,
  onSizePriceChange,
  onModalChangeState,
  isModalOpen,
  isEditing,
  options,
  isConfirmButtonDisabled,
  onSizeDelete,
  onDragOptionEnd,
  isLoading,
  sizeName,
  onToastClose,
  onSizeNameChange,
  onEditSubmit,
  onEditCancel,
  hasServerError,
  onOptionDetach,
}) => {
  const handleKeyPress = useCallback(
    isEditing && !isConfirmButtonDisabled ? handleEnterKeyPress(onEditSubmit) : null,
    [isEditing, isConfirmButtonDisabled, onEditSubmit],
  );

  return (
    <Edit
      error={error}
      name={sizeName}
      onDelete={onSizeDelete}
      isEditing={isEditing}
      onModalChangeState={onModalChangeState}
      isModalOpen={isModalOpen}
      isConfirmButtonDisabled={isConfirmButtonDisabled}
      isLoading={isLoading}
      onToastClose={onToastClose}
      onEditCancel={onEditCancel}
      onEditSubmit={onEditSubmit}
      hasServerError={hasServerError}
    >
      <Input
        marginTop="xl2"
        marginBottom="xl4"
        type="text"
        id="sizeName"
        name="sizeName"
        placeholder="Size name"
        value={sizeName}
        onChange={onSizeNameChange}
        onKeyPress={handleKeyPress}
        data-cy="sizeName"
      />
      <Input
        marginBottom="xl5"
        type="text"
        id="sizePrice"
        name="sizePrice"
        placeholder="Size initial price"
        value={`$${sizePrice}`}
        onChange={onSizePriceChange}
        onKeyPress={handleKeyPress}
        data-cy="sizePrice"
      />
      <Flex selfCenter itemsCenter>
        <Checkbox onCheckedStateChange={onSizePreselectedStateChange} isChecked={isSizePreselected} />
        <Typography marginLeft="lg" size="xl3" weight="black">Is size preselected?</Typography>
      </Flex>
      <Flex itemsCenter column paddingTop="xl5">
        <Typography marginBottom="md" size="xl2" weight="black">
          Options of current size
        </Typography>
        {!options.length && !isEditing && (
          <Flex justifyStart>
            <Typography size="lg" color="warmGrey" weight="black" marginBottom="lg">
              Nothing here yet
            </Typography>
          </Flex>
        )}
        <DragDropContext onDragEnd={onDragOptionEnd}>
          <Droppable droppableId="sizeOptions">
            {provided => (
              <Flex column ref={provided.innerRef} {...provided.droppableProps}>
                <DraggableItems
                  isDataTheSame={isDataTheSame}
                  canBeAdded={isEditing}
                  onItemClick={onOptionEdit}
                  onItemAdd={onOptionAdd}
                  onItemRemove={onOptionDetach}
                  items={options}
                />
                {provided.placeholder}
              </Flex>
            )}
          </Droppable>
        </DragDropContext>
      </Flex>
      <EditWarning isWarningOpen={isWarningOpen} onWarningClose={onWarningChangeState} />
    </Edit>
  );
};

SizeProcess.propTypes = {
  isDataTheSame: bool.isRequired,
  isWarningOpen: bool.isRequired,
  onWarningChangeState: func.isRequired,
  onOptionEdit: func.isRequired,
  error: string.isRequired,
  onSizePreselectedStateChange: func.isRequired,
  isSizePreselected: bool.isRequired,
  sizePrice: string.isRequired,
  onSizePriceChange: func.isRequired,
  isModalOpen: bool.isRequired,
  onModalChangeState: func.isRequired,
  isEditing: bool.isRequired,
  isConfirmButtonDisabled: bool.isRequired,
  onSizeDelete: func.isRequired,
  onDragOptionEnd: func.isRequired,
  options: arrayOf(exact({
    name: string,
    id: number,
  })).isRequired,
  onToastClose: func.isRequired,
  hasServerError: bool.isRequired,
  isLoading: bool.isRequired,
  sizeName: string.isRequired,
  onSizeNameChange: func.isRequired,
  onEditSubmit: func.isRequired,
  onEditCancel: func.isRequired,
  onOptionAdd: func.isRequired,
  onOptionDetach: func.isRequired,
};

export default SizeProcess;
